import { useEffect, useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
} from "recharts";
import { formatInr } from "../utils/accountsAnalytics";

function axisCurrency(value) {
  const amount = Number(value) || 0;
  if (amount >= 10000000) return `${(amount / 10000000).toFixed(1)}Cr`;
  if (amount >= 100000) return `${(amount / 100000).toFixed(1)}L`;
  if (amount >= 1000) return `${(amount / 1000).toFixed(0)}k`;
  return `${amount}`;
}

function renderNoData(title, subtitle) {
  return (
    <div className="bg-white rounded-lg shadow p-4 flex items-center justify-center">
      <div className="text-center">
        <h3 className="font-semibold text-gray-700">{title}</h3>
        <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
      </div>
    </div>
  );
}

export default function Charts({ analytics, loading }) {
  const monthOptions = useMemo(
    () => analytics?.monthOptions || [],
    [analytics?.monthOptions],
  );
  const monthlySeries = useMemo(
    () => analytics?.monthlySeries || [],
    [analytics?.monthlySeries],
  );
  const [selectedMonthKey, setSelectedMonthKey] = useState("");

  useEffect(() => {
    if (monthOptions.length === 0) {
      setSelectedMonthKey("");
      return;
    }

    const monthKeys = monthOptions.map((item) => item.key);
    const preferred =
      monthKeys.find((key) => key === analytics?.currentMonthKey) ||
      monthKeys[monthKeys.length - 1];

    if (!selectedMonthKey || !monthKeys.includes(selectedMonthKey)) {
      setSelectedMonthKey(preferred);
    }
  }, [analytics?.currentMonthKey, monthOptions, selectedMonthKey]);

  const dailySeries = useMemo(() => {
    if (!selectedMonthKey) return [];
    return analytics?.dailySeriesByMonth?.[selectedMonthKey] || [];
  }, [analytics, selectedMonthKey]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mt-6">
        {renderNoData("Loading Charts", "Calculating account statistics...")}
        {renderNoData("Loading Charts", "Preparing daily payment trend...")}
      </div>
    );
  }

  if (monthlySeries.length === 0) {
    return (
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mt-6">
        {renderNoData(
          "No Accounts Data",
          "Save payment records to see monthly analytics.",
        )}
        {renderNoData("No Daily Data", "No payment history available yet.")}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mt-6">
      <div className="xl:col-span-2 bg-white rounded-lg shadow p-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold">Monthly Fee Collection (Accounts)</h3>
          <span className="text-xs text-gray-500">
            Based on `received_payment` history
          </span>
        </div>

        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthlySeries}>
              <XAxis dataKey="label" />
              <YAxis tickFormatter={axisCurrency} />
              <Tooltip
                formatter={(value) => [formatInr(value), "Collection"]}
                labelFormatter={(label) => `Month: ${label}`}
              />
              <Bar dataKey="collection" fill="#22c55e" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex justify-between items-center mb-4 gap-2">
          <h3 className="font-semibold">Daily Payments</h3>
          <select
            value={selectedMonthKey}
            onChange={(event) => setSelectedMonthKey(event.target.value)}
            className="border rounded px-2 py-1 text-sm bg-white"
          >
            {monthOptions.map((item) => (
              <option key={item.key} value={item.key}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={dailySeries}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis tickFormatter={axisCurrency} />
              <Tooltip
                formatter={(value) => [formatInr(value), "Collection"]}
                labelFormatter={(label) => `Day ${label}`}
              />
              <Line
                type="monotone"
                dataKey="collection"
                stroke="#2563eb"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
